/**
 * Unregistered equipment: the country selector, the asset types, and the
 * per-record meter labels.
 *
 * The rule the labels follow is that they resolve from what a record TRACKS,
 * never from what it is. An hour-tracked machine may also be a registered
 * vehicle, so "equipment" is the wrong question to ask when choosing between
 * "Kilometrit" and "Tunnit".
 */

export const REGISTRATION_COUNTRY_FI = 'FI';
export const REGISTRATION_COUNTRY_UNREGISTERED = 'UNREGISTERED';

/**
 * Data-driven so Sweden and Norway can be switched on with one `enabled: true`
 * once those registries are integrated. "Ei rek." stays last.
 */
export const REGISTRATION_COUNTRIES = [
  { value: REGISTRATION_COUNTRY_FI, labelKey: 'registrationCountryFI', enabled: true },
  { value: REGISTRATION_COUNTRY_UNREGISTERED, labelKey: 'registrationCountryUnregistered', enabled: true },
];

export const getRegistrationCountryOptions = (t) =>
  REGISTRATION_COUNTRIES
    .filter((country) => country.enabled)
    .map((country) => ({ value: country.value, label: t(country.labelKey) }));

/**
 * The STRUCTURAL question only: road vehicle or machine. It decides which list a
 * record appears in and which rules apply.
 *
 * The descriptive category (forklift, scissor lift, ...) is deliberately
 * absent. Nothing branches on it, customers disagree about the categories, and
 * it waits on Jari rather than being guessed here.
 */
export const ASSET_TYPE_VEHICLE = 'vehicle';
export const ASSET_TYPE_EQUIPMENT = 'equipment';

export const getAssetTypeLabel = (assetType, t) =>
  (assetType === ASSET_TYPE_EQUIPMENT
    ? t('assetTypeEquipment', { ns: 'common' })
    : t('assetTypeVehicle', { ns: 'common' }));

export const isEquipment = (vehicle) =>
  !!vehicle && !!vehicle.asset_type && vehicle.asset_type !== ASSET_TYPE_VEHICLE;

export const isUnregistered = (vehicle) =>
  !!vehicle && vehicle.registration_country === REGISTRATION_COUNTRY_UNREGISTERED;

/**
 * Which meter labels this record should use.
 *
 * A record with neither flag (a trailer) is treated as kilometre-tracked so the
 * UI keeps its existing shape rather than losing its labels entirely.
 *
 * `t` must be bound to the VehicleDetailsView namespace, which is where every
 * key below lives.
 */
export const getMeterLabels = (vehicle, t) => {
  const tracksHours = !!(vehicle && vehicle.tracks_hours);
  const tracksKm = !vehicle || vehicle.tracks_km !== false;

  if (tracksHours && !tracksKm) {
    return {
      mode: 'hours',
      unit: t('hoursUnit'),
      tabLabel: t('tasksHours'),
      sectionLabel: t('hoursHeading'),
      infoboxLabel: t('hoursInfobox'),
      readingLabel: t('hoursReadingLabel'),
      addReadingLabel: t('addHoursLabel'),
      valueLabel: t('hoursValue'),
    };
  }

  if (tracksHours && tracksKm) {
    // Both meters: neutral headings, and each row shows its own unit.
    return {
      mode: 'both',
      unit: '',
      tabLabel: t('tasksMileageAndHours'),
      sectionLabel: t('mileageAndHoursHeading'),
      infoboxLabel: t('mileageAndHoursInfobox'),
      readingLabel: t('meterReadingLabel'),
      addReadingLabel: t('addMeterReadingLabel'),
      valueLabel: t('meterValue'),
    };
  }

  return {
    mode: 'km',
    unit: t('kilometersUnit'),
    tabLabel: t('tasksMileage'),
    sectionLabel: t('mileagesHeading'),
    infoboxLabel: t('kilometersInfobox'),
    readingLabel: t('manualKmLabel'),
    addReadingLabel: t('addMileageLabel'),
    valueLabel: t('mileageValue'),
  };
};

/**
 * The unit suffix of ONE reading. Readings of both meters share a list, so the
 * suffix follows the reading itself, not the record. 'h' and 'km' are spelled
 * the same in fi/en/sv, which lets the list components that have no `t` use it.
 */
export const getReadingUnitLabel = (reading) => (reading && reading.unit === 'h' ? 'h' : 'km');

/** The unit a new reading defaults to for this record. */
export const getDefaultReadingUnit = (vehicle) =>
  (vehicle && vehicle.tracks_hours && vehicle.tracks_km === false) ? 'h' : 'km';

/**
 * What the care-service add dialog does with the machines a search found.
 *
 * - none: nothing matched among the customers this workshop or chain serves
 * - unavailable: everything that matched is already in a care service
 * - single: exactly one match, ready to add without asking
 * - choose: several customers have a machine with this identifier; the user
 *   picks one. Matches already in a care service are shown but not offered.
 */
export const resolveCandidateChoice = (candidates) => {
  const all = candidates || [];
  const selectable = all.filter((candidate) => !candidate.already_in_care_service);

  if (all.length === 0) return { step: 'none', vehicleId: null };
  if (selectable.length === 0) return { step: 'unavailable', vehicleId: null };
  if (all.length === 1) return { step: 'single', vehicleId: selectable[0].vehicle_id };
  return { step: 'choose', vehicleId: null };
};

/** "Trukki 1 — Kuljetus Oy / Halli · Toyota 8FBE15" */
export const formatCandidateLabel = (candidate) => {
  const owner = [candidate.organization_name, candidate.garage_name].filter(Boolean).join(' / ');
  const model = [candidate.manufacturer, candidate.model_name].filter(Boolean).join(' ');
  return [candidate.identifier, owner].filter(Boolean).join(' — ') + (model ? ` · ${model}` : '');
};

